<script setup lang="ts">
import { adaptMapboxFeatureToAddressBody, type AddressBody, MAPBOX_API_CONFIG, type MapboxGeocodeResponse } from '@lib/shared';
import lodashToString from 'lodash-es/toString';

// PROPS
interface Props {
    disabled?: boolean;
}

const { disabled = false } = defineProps<Props>();

// MODEL
const model = defineModel<AddressBody | undefined>({ required: true });

// COMPOSABLES
const { mapboxAccessToken } = useRuntimeConfig().public;
const { notifyError } = useNotification();
const { tt } = useTypedI18n();
const { logError } = useLogs();

// DATA
const searchTerm = ref('');
const searchTermDebounced = refDebounced(searchTerm, 200);

// NOTICE: await in useLazyFetch with immediate:false cause a leak because the promise never resolve!
// await on useLazyFetch must be used ONLY if you need to stop navigation until resolve.
// @see https://adorea.atlassian.net/browse/MAIN-295?focusedCommentId=38388
const { data, status, error, execute } = useLazyFetch(MAPBOX_API_CONFIG.geocodeForward.url, {
    params: { ...MAPBOX_API_CONFIG.geocodeForward.params, q: searchTermDebounced, access_token: mapboxAccessToken },
    transform: (data: MapboxGeocodeResponse): Partial<AddressBody>[] => {
        return (data?.features ?? []).map(feature => adaptMapboxFeatureToAddressBody(feature));
    },
    immediate: false,
    server: false,
});
const isLoading = computed(() => status.value === 'pending');
const items = computed<Partial<AddressBody>[]>(() => data.value ?? []);

// WATCHERS
watch(error, value => {
    const errorMessage = isFetchError(value) ? value.data.message : lodashToString(value);
    logError('Error searching fetching mapbox address', { error: errorMessage });
    notifyError({ title: tt('notifications.genericError'), description: tt('vas.LogisticsService.validation.apiError') });
});
watch(searchTermDebounced, value => !!value && execute());
</script>

<template>
    <UInputMenu
        v-model="model"
        v-model:search-term="searchTerm"
        :loading="isLoading"
        :items="items"
        icon="lucide:map-pin"
        autocomplete="off"
        size="xl"
        :disabled="disabled"
        label-key="fullAddress"
        ignore-filter
        class="w-full"
    />
</template>
